// CKEditor
import { createPortal } from 'react-dom'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
    ClassicEditor,
    AccessibilityHelp,
    Alignment,
    AutoLink,
    Autosave,
    Bold,
    Essentials,
    FontColor,
    FontBackgroundColor,
    Indent,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Italic,
    LinkEditing,
    List,
    Paragraph,
    SelectAll,
    Underline,
    Undo,
    Mention,
    PasteFromOffice,
    Clipboard,
    Table,
    TableToolbar,
    TableCellProperties,
    RemoveFormat,
    ImageUtils,
    ImageEditing
} from 'ckeditor5';
import { MergeFields, PasteFromOfficeEnhanced } from 'ckeditor5-premium-features'
import 'ckeditor5/ckeditor5.css'
import 'ckeditor5-premium-features/ckeditor5-premium-features.css'
import './InlineCkEditor.scoped.scss'
import stripStylesOnPaste from './stripStylesOnPaste'
import textColors from './textColors'
import PillEditor from 'Shared/components/PillEditor/PillEditor'

// CKEditor plugins
import MergeTagsPlugin from './MergeTagsPlugin'
import CustomLinkPlugin from './CustomLinkPlugin'

// merge tags
import Modal from 'Shared/components/Modal/Modal'
import MergeTagList from 'Shared/components/PillEditor/MergeTagList.jsx'
import CodeMirrorEditor from 'Shared/components/CodeMirrorEditor'

export default function InlineCkEditor({
    mergeTags=[],
    value,
    onUpdate,
    sourceMode,
    setSourceMode,
    disabled = false,
    features = ['sourceMode'],
    hideMergeTags = false,
    placeholder = 'Type some content here!'
}) {
    const [showMergeTagsModal, setShowMergeTagsModal] = useState(false)
    const [editorInstance, setEditorInstance] = useState(null)
    const [linkTextPillEditorMount, setLinkTextPillEditorMount] = useState(null)
    const [linkUrlPillEditorMount, setLinkUrlPillEditorMount] = useState(null)
    const [bodyWrapper, setBodyWrapper] = useState(null)
    const isFeatureEnabled = (feature) => { return features.includes(feature) }
    const onUpdateRef = useRef(onUpdate)
    onUpdateRef.current = onUpdate

    const getToolbarItems = () => {
        const baseToolbarItems = [
            'fontSize',
            'fontFamily',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'removeFormat',
            '|',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'customLink',
            '|',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
        ];

        if (isFeatureEnabled('tables')) {
            baseToolbarItems.push('|', 'insertTable');
        }

        if (!hideMergeTags) {
            baseToolbarItems.push('-', 'mergeTags');
        }

        return baseToolbarItems;
    }

    const getPlugins = () => {
        const basePlugins = [
            AccessibilityHelp,
            Alignment,
            AutoLink,
            Autosave,
            Bold,
            Clipboard,
            Essentials,
            FontColor,
            FontBackgroundColor,
            Indent,
            FontFamily,
            FontSize,
            GeneralHtmlSupport,
            Italic,
            LinkEditing,
            CustomLinkPlugin,
            List,
            Paragraph,
            SelectAll,
            Underline,
            Undo,
            MergeFields,
            ImageUtils, // required by merge fields
            ImageEditing, // required by merge fields
            Mention,
            MergeTagsPlugin,
            PasteFromOffice,
            PasteFromOfficeEnhanced,
            RemoveFormat
        ]

        if (isFeatureEnabled('tables')) {
            basePlugins.push(Table, TableToolbar, TableCellProperties)
        }

        return basePlugins
    }

    const onReady = (editor) => {
        setEditorInstance(editor)
        stripStylesOnPaste(editor)
    }

    const handleChange = (_event, editor) => {
        onUpdateRef.current(editor.getData())
    }

    const handleInsertTag = (tagText) => {
        if (!editorInstance) return
        editorInstance.execute('insertMergeTag', tagText)
    }

    useEffect(() => {
        if (editorInstance) {
            editorInstance.editing.view.document.getRoot('main').placeholder = placeholder
        }
    }, [placeholder])

    useEffect(() => {
        // Find the .ck-body-wrapper element and set it for portal rendering
        // This is so that the merge tags modal is rendered outside of the editor container, above any ckeditor popups
        const wrapper = document.querySelector('.ck-body-wrapper')
        if (wrapper) {
            setBodyWrapper(wrapper)
        }
    }, [])

    return <>
        <div className='editor-container'>
        {isFeatureEnabled('sourceMode') && <div className='row'>
            <div className='tabs'>
                <div className={`tab ${sourceMode ? '' : 'selected'}`} onClick={() => setSourceMode(false)}>Visual</div>
                <div className={`tab ${sourceMode ? 'selected' : ''}`} onClick={() => setSourceMode(true)}>HTML</div>
            </div>
        </div>}
        {sourceMode && <CodeMirrorEditor value={value} onChange={onUpdate} disabled={disabled} />}
        {!sourceMode && <CKEditor
            editor={ClassicEditor}
            disabled={disabled}
            onChange={handleChange}
            onReady={onReady}
            data={value}
            config={{
                width: '100%',
                placeholder: placeholder || 'Type some content here!',
                toolbar: {
                    items: getToolbarItems(),
                    shouldNotGroupWhenFull: true
                },
                plugins: getPlugins(),
                fontFamily: {
                    options: [
                        'Arial, sans-serif',
                        'Helvetica, sans-serif',
                        'Optima, sans-serif',
                        'Futura, sans-serif'
                    ],
                    supportAllValues: true
                },
                fontColor: {
                    ...textColors,
                    colorPicker: {
                        format: 'hex'
                    }
                },
                fontBackgroundColor: {
                    ...textColors,
                    colorPicker: {
                        format: 'hex'
                    }
                },
                fontSize: {
                    options: [10, 12, 14, 'default', 18, 20, 22, 24],
                    supportAllValues: true
                },
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
                    // The default styles for table cells in the editor.
                    // They should be synchronized with the content styles.
                    tableCellProperties: {
                        defaultProperties: {
                            borderStyle: 'none',
                            borderColor: '',
                            borderWidth: '',
                        },
                        borderColors: textColors.colors.map(color => ({
                            color: color.color,
                            label: color.label,
                            hasBorder: color.hasBorder || false
                        })),
                        backgroundColors: textColors.colors.map(color => ({
                            color: color.color,
                            label: color.label,
                            hasBorder: color.hasBorder || false
                        })),
                        colorPicker: {
                            format: 'hex'
                        }
                    }
                },
                htmlSupport: {
                    allow: [
                        {
                            name: /^.*$/,
                            styles: true,
                            attributes: true,
                            classes: true
                        }
                    ],
                    disallow: [
                        {
                            name: /^(div|section|article|script)$/
                        },
                        {
                            name: 'img',
                            attributes: {
                                src: /^data:/
                            }
                        }
                    ]
                },
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://'
                },
                licenseKey: window.ENV.CKEDITOR_KEY,
                mention: {},
                // Custom plugin configurations
                mergeTagsUI: {
                    handleVisibleModal: bool => setShowMergeTagsModal(bool),
                },
                customLinkUI: {
                    disabled: false,
                    onLinkTextPillEditorMount: payload => setLinkTextPillEditorMount(payload),
                    onLinkTextPillEditorUnmount: () => setLinkTextPillEditorMount(null),
                    onLinkUrlPillEditorMount: payload => setLinkUrlPillEditorMount(payload),
                    onLinkUrlPillEditorUnmount: () => setLinkUrlPillEditorMount(null)
                },
            }}
        />}
        </div>
        {linkTextPillEditorMount?.container && createPortal(
            <PillEditor
                singleLine
                condensed
                placeholder='Link text'
                initialValue={linkTextPillEditorMount.initialValue}
                onChange={linkTextPillEditorMount.onChange}
                availableTags={mergeTags}
            />,
            linkTextPillEditorMount.container
        )}
        {linkUrlPillEditorMount?.container && createPortal(
            <PillEditor
                singleLine
                condensed
                webLink={true}
                placeholder='Link URL'
                initialValue={linkUrlPillEditorMount.initialValue}
                onChange={linkUrlPillEditorMount.onChange}
                availableTags={mergeTags}
            />,
            linkUrlPillEditorMount.container
        )}
        {!hideMergeTags && bodyWrapper && createPortal(
            <div>
                <Modal open={showMergeTagsModal} title="Merge Tags" onCancel={() => setShowMergeTagsModal(false)} extraClass="white-content" autoHeight>
                    <MergeTagList
                        mergeTags={mergeTags}
                        popup={false}
                        mode={'slug'}
                        insert={handleInsertTag}
                        close={() => setShowMergeTagsModal(false)}
                    />
                </Modal>
            </div>,
            bodyWrapper
        )}
    </>
}
